const STORAGE_KEY = 'cloudSyncAuth'
export const GOOGLE_TOKEN_EVENT = 'googleTokenUpdated'
export const GOOGLE_TOKEN_REFRESH_EVENT = 'googleTokenRefreshNeeded'
// Google Identity Services access tokens expire after ~1 hour. The previous
// 12-hour TTL kept dead tokens "alive" client-side, so requests waited until
// a 401 to discover the expiry and only then cleared auth. 55 minutes is a
// safe pre-expiry window that lets a sign-in re-prompt happen before any
// sync attempt fails server-side.
export const GOOGLE_TOKEN_TTL_MS = 55 * 60 * 1000 // 55 minutes
// Threshold for "expiring soon" — used to proactively prompt re-auth.
export const GOOGLE_TOKEN_EXPIRING_SOON_MS = 5 * 60 * 1000 // 5 minutes

const hasBrowserEnv = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const getStorage = () => {
  if (!hasBrowserEnv) {
    return null
  }
  return window.localStorage
}

const broadcast = (detail) => {
  if (!hasBrowserEnv) {
    return
  }
  window.dispatchEvent(new CustomEvent(GOOGLE_TOKEN_EVENT, { detail }))
}

export const persistGoogleAuth = (accessToken, userId) => {
  const storage = getStorage()
  if (!storage) {
    return null
  }

  if (!accessToken || !userId) {
    clearGoogleAuth()
    return null
  }

  const payload = {
    accessToken,
    userId,
    timestamp: Date.now()
  }

  storage.setItem(STORAGE_KEY, JSON.stringify(payload))
  broadcast(payload)
  return payload
}

export const getStoredGoogleAuth = () => {
  const storage = getStorage()
  if (!storage) {
    return null
  }

  const raw = storage.getItem(STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    const payload = JSON.parse(raw)
    if (!payload?.accessToken) {
      clearGoogleAuth()
      return null
    }

    const tokenAge = Date.now() - (payload.timestamp || 0)
    if (tokenAge > GOOGLE_TOKEN_TTL_MS) {
      clearGoogleAuth()
      return null
    }

    return payload
  } catch (error) {
    clearGoogleAuth()
    return null
  }
}

export const clearGoogleAuth = () => {
  const storage = getStorage()
  if (!storage) {
    return
  }
  storage.removeItem(STORAGE_KEY)
  broadcast(null)
}

export const getStorageKey = () => STORAGE_KEY

// Returns ms until the stored token hits the TTL boundary, or null if no
// token. Negative numbers mean already expired by client-side reckoning.
export const getStoredTokenRemainingMs = () => {
  const storage = getStorage()
  if (!storage) return null
  const raw = storage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const payload = JSON.parse(raw)
    if (!payload?.timestamp) return null
    return GOOGLE_TOKEN_TTL_MS - (Date.now() - payload.timestamp)
  } catch {
    return null
  }
}

// True when the stored token is within the EXPIRING_SOON window. UI/sync
// layers can listen for the GOOGLE_TOKEN_REFRESH_EVENT or poll this to
// trigger a silent re-auth before the next request 401s.
export const isStoredTokenExpiringSoon = () => {
  const remaining = getStoredTokenRemainingMs()
  if (remaining === null) return false
  return remaining < GOOGLE_TOKEN_EXPIRING_SOON_MS
}

// Fire the refresh-needed event. Idempotent — listeners are responsible
// for de-duping concurrent triggers.
export const requestTokenRefresh = (reason = 'expiring') => {
  if (!hasBrowserEnv) return
  window.dispatchEvent(new CustomEvent(GOOGLE_TOKEN_REFRESH_EVENT, {
    detail: { reason, requestedAt: Date.now() }
  }))
}

const googleTokenService = {
  persistGoogleAuth,
  getStoredGoogleAuth,
  clearGoogleAuth,
  getStorageKey,
  getStoredTokenRemainingMs,
  isStoredTokenExpiringSoon,
  requestTokenRefresh,
}

export default googleTokenService
